import HotjarFunctions from '@analytics/hotjar/functions';
import KlaviyoFunctions from '@analytics/klaviyo/functions';
import Mixpanel from '@analytics/mixpanel/Mixpanel';
import { BaseInput } from '@analytics/mixpanel/types/Mixpanel.types';
import {
  JoinNotifyListButtonClickedInput,
  JoinNotifyListFailedInput,
  NotifyMeAPICalledInput,
  NotifyMeInput,
  OnTabChangeOrCTAInput,
  ProfilePageBaseInput,
  RequestLimitingInput,
  ShareInput,
} from '@analytics/mixpanel/types/ProfilePage.types';
import { getLoggedInStatus } from '@common/utils';

export const onProfilePageLoaded = ({ router, celebrityDetails }: ProfilePageBaseInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Profile Page Loaded`);
  Mixpanel.track(
    `Profile Page Loaded`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Celebrity Name': celebrityDetails.celebrityName,
      },
    }),
  );
};

export const onTotalRequestLimitCardClicked = ({ router, details }: RequestLimitingInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Total Request Limit Card Clicked`);
  Mixpanel.track(
    `Total Request Limit Card Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Celebrity Name': details.celebrityName,
        'Limited Requests Total': details.total,
        'Video Requests Remaining': details.videoRemaining,
        'Audio Requests Remaining': details.audioRemaining,
      },
    }),
  );
};

export const onMessageTypeTabChanged = ({ router, details }: OnTabChangeOrCTAInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Message Type Tab Changed`);
  Mixpanel.track(
    `Message Type Tab Changed`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Message Type': details.messageType,
        'Celebrity Name': details.celebrityName,
        Price: details.price,
      },
    }),
  );
};

export const onMessageTypeRemainingClicked = ({ router, details }: RequestLimitingInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Message Type Limit Card Clicked`);
  Mixpanel.track(
    `Message Type Limit Card Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Message Type': details.messageType,
        'Celebrity Name': details.celebrityName,
        'Limited Requests Total': details.total,
        'Video Requests Remaining': details.videoRemaining,
        'Audio Requests Remaining': details.audioRemaining,
      },
    }),
  );
};

export const onGetMessageClicked = ({ router, details }: OnTabChangeOrCTAInput): void => {
  HotjarFunctions.addTag('get_message_clicked');
  KlaviyoFunctions.addEvent('Get Message Clicked', {
    'Message Type': details.messageType,
    'Celebrity Name': details.celebrityName,
    Price: details.price,
  });
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Get Message Clicked`);
  Mixpanel.people.increment(`Get Message Clicked Count`);
  Mixpanel.track(
    `Get Message Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Message Type': details.messageType,
        'Celebrity Name': details.celebrityName,
        Price: details.price,
      },
    }),
  );
};

export const onNotifyMeAPICalled = ({ router, details }: NotifyMeAPICalledInput): void => {
  const loggedInStatus = getLoggedInStatus();
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Notify Me API Called`);
  Mixpanel.people.increment(`Notify Me API Called Count`);
  Mixpanel.track(
    `Notify Me API Called`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        Email: details.email,
        'Message Types': details.messageTypes,
        'Celebrity Name': details.celebrityName,
        'Logged In Status': loggedInStatus,
      },
    }),
  );
};

export const onNotifyMeButtonClicked = ({ router, details }: NotifyMeInput): void => {
  HotjarFunctions.addTag('notify_me_button_clicked');
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Notify Me Button Clicked`);
  Mixpanel.people.increment(`Notify Me Button Clicked Count`);
  Mixpanel.track(
    `Notify Me Button Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Message Types': details.messageTypes,
        'Celebrity Name': details.celebrityName,
      },
    }),
  );
};

export const onJoinNotifyListButtonClicked = ({ router, details }: JoinNotifyListButtonClickedInput): void => {
  HotjarFunctions.addTag('join_notify_list_button_clicked');
  Mixpanel.track(
    `Join Notify List Button Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Message Type': details.messageType,
        'Celebrity Name': details.celebrityName,
      },
    }),
  );
};

export const onJoinNotifyListFailed = ({ router, details }: JoinNotifyListFailedInput): void => {
  const additionalProperties: any = {
    'Message Type': details.messageType,
    'Celebrity Name': details.celebrityName,
    'Failure Type': details.failureInfo.type,
  };

  Mixpanel.track(
    `Join Notify List Failed`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties,
    }),
  );
};

export const onProfileShareButtonClicked = ({ router, details }: ShareInput): void => {
  const loggedInStatus = getLoggedInStatus();
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Profile Share Button Clicked`);
  Mixpanel.track(
    `Profile Share Button Clicked`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Share Method': details.sharePlatform,
        'Celebrity Name': details.celebrityName,
        'Limited Requests Total': details.total,
        'Video Requests Remaining': details.videoRemaining,
        'Audio Requests Remaining': details.audioRemaining,
        'Logged In Status': loggedInStatus,
      },
    }),
  );
};

export const onProfileVideoPlayed = ({ router, celebrityDetails }: ProfilePageBaseInput): void => {
  const loggedInStatus = getLoggedInStatus();
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Profile Video Played`);
  Mixpanel.people.increment(`Profile Video Played Count`);
  Mixpanel.track(
    `Profile Video Played`,
    Mixpanel.createEventProperties({
      router,
      additionalProperties: {
        'Celebrity Name': celebrityDetails.celebrityName,
        'Logged In Status': loggedInStatus,
      },
    }),
  );
};

export const onCelebBioExpanded = ({ router }: BaseInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Celeb Bio Expanded`);
  Mixpanel.track(`Celeb Bio Expanded`, Mixpanel.createEventProperties({ router }));
};

export const onCelebBioCollapsed = ({ router }: BaseInput): void => {
  Mixpanel.people.set(`Date of Last Event`, new Date().toUTCString());
  Mixpanel.people.set(`Last Event`, `Celeb Bio Collapsed`);
  Mixpanel.track(`Celeb Bio Collapsed`, Mixpanel.createEventProperties({ router }));
};
